import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import apiService from "../services/api";
import ImageViewerModal from "../components/ImageViewerModal";
import TourHelpButton from "../components/TourHelpButton";
import { useProductTour } from "../onboarding/useProductTour";
import { TOUR_IDS } from "../onboarding/tours";
import analytics, { errorCode } from "../services/analytics";
import "../styles.css";

const PAGE_SIZE = 6;

const formatDateTime = (value) => {
  if (!value) return { date: "Unknown date", time: "" };
  try {
    const d = new Date(value);
    return {
      date: d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      time: d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  } catch {
    return { date: String(value), time: "" };
  }
};

const SessionCard = ({ session, isFirst, onView }) => {
  const { date, time } = formatDateTime(session.capturedAt);

  return (
    <motion.section
      data-tour={isFirst ? "capture-session" : undefined}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 mb-6"
    >
      {/* Session header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-white font-semibold">
            {date}
            {time && <span className="text-gray-300 font-normal"> • {time}</span>}
          </div>
          {session.place && (
            <div className="text-gray-300 text-sm mt-1">📍 {session.place}</div>
          )}
          {session.dnaBarcoded && (session.genus || session.species) && (
            <div className="text-yellow-100 text-sm mt-1 italic">
              {[session.genus, session.species].filter(Boolean).join(" ")}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {session.dnaBarcoded ? (
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-100 border border-yellow-400/40">
              DNA barcoded
            </span>
          ) : (
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-100 border border-indigo-400/40">
              Not DNA barcoded
            </span>
          )}
        </div>
      </div>

      {/* Two cameras */}
      <div
        className="grid sm:grid-cols-2 gap-4"
        data-tour={isFirst ? "capture-cameras" : undefined}
      >
        {session.cameras && session.cameras.length > 0 ? (
          session.cameras.map((cam) => {
            const url = cam.signedUrl || cam.gcsUrl;
            return (
              <div
                key={cam.position}
                className="bg-black/40 rounded-xl overflow-hidden border border-white/10 flex flex-col"
              >
                <div className="aspect-video bg-black/60 flex items-center justify-center overflow-hidden">
                  <img
                    src={url}
                    alt={`Camera #${cam.position}`}
                    className="h-full w-full object-contain object-center"
                    loading="lazy"
                    decoding="async"
                    onLoad={(event) => {
                      if (!event.currentTarget.dataset.failed) {
                        analytics.capture("image_load_completed", { source: "capture_card" });
                      }
                    }}
                    onError={(e) => {
                      if (e.currentTarget.dataset.failed) return;
                      e.currentTarget.dataset.failed = "true";
                      analytics.capture("image_load_failed", { source: "capture_card" });
                      e.currentTarget.src =
                        "https://via.placeholder.com/800x600?text=Image+Not+Available";
                    }}
                  />
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  <span className="text-white text-sm font-medium">
                    Camera #{cam.position}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      onView({
                        src: url,
                        alt: `Camera #${cam.position} from ${date}${time ? ` at ${time}` : ""}`,
                        title: `${date}${time ? ` • ${time}` : ""} — Camera #${cam.position}`,
                      })
                    }
                    className="py-1.5 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm border border-white/20"
                  >
                    View
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-gray-400 text-sm italic col-span-2 py-6 text-center">
            No processed images available for this session yet.
          </div>
        )}
      </div>
    </motion.section>
  );
};

const PaginationControls = ({
  page,
  totalPages,
  canPrev,
  canNext,
  isLoading,
  onPageChange,
  className = "",
  tourTarget,
}) => (
  <div
    className={`flex items-center justify-center gap-3 ${className}`}
    data-tour={tourTarget}
  >
    <button
      type="button"
      onClick={() => canPrev && onPageChange(page - 1)}
      disabled={!canPrev || isLoading}
      className="rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
    >
      ← Previous
    </button>
    <span className="text-sm text-gray-300">
      Page {page} of {totalPages}
    </span>
    <button
      type="button"
      onClick={() => canNext && onPageChange(page + 1)}
      disabled={!canNext || isLoading}
      className="rounded-lg border border-white/20 bg-white/10 px-4 py-2 text-sm text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-40"
    >
      Next →
    </button>
  </div>
);

const CapturedImagesPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewerImage, setViewerImage] = useState(null);
  const galleryTopRef = useRef(null);
  const { isTourRunning, startTour } = useProductTour({
    tourId: TOUR_IDS.captures,
    userId: user?.id,
    ready: Boolean(user) && !isLoading,
  });

  const loadPage = useCallback(async (targetPage) => {
    const started = performance.now();
    setIsLoading(true);
    setError("");
    try {
      const data = await apiService.getCaptureSessions(PAGE_SIZE, targetPage);
      setSessions(data.sessions || []);
      setPagination(data.pagination || { page: targetPage, totalPages: 1, total: 0 });
      analytics.capture("capture_page_loaded", {
        page: targetPage,
        session_count: data.sessions?.length || 0,
        duration_ms: Math.round(performance.now() - started),
      });
    } catch (err) {
      analytics.capture("capture_page_failed", { page: targetPage, error_code: errorCode(err) });
      console.error("Failed to load capture sessions:", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to load images. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = apiService.onAuthChange((profile) => {
      if (!profile) {
        navigate("/auth");
        return;
      }
      if (profile.role !== "EXPERT") {
        navigate("/pending");
        return;
      }
      setUser(profile);
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (apiService.isAuthenticated()) {
      loadPage(page);
    }
  }, [page, loadPage]);

  const handleLogout = async () => {
    await apiService.logout();
    navigate("/");
  };

  const totalPages = Math.max(1, pagination.totalPages || 1);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const handlePageChange = (nextPage) => {
    analytics.capture("capture_pagination_used", { page: nextPage });
    setPage(nextPage);
    galleryTopRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <nav className="p-4 sm:p-6 flex flex-wrap gap-4 justify-between items-center bg-black/30 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-4">
          <button
            onClick={() => navigate("/dashboard")}
            className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm"
          >
            ← Dashboard
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Trap Captured Images</h1>
            {user && (
              <p className="text-gray-300 text-sm">{user.username}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TourHelpButton onClick={startTour} isRunning={isTourRunning} />
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
          >
            Logout
          </button>
        </div>
      </nav>

      <div ref={galleryTopRef} className="container mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200">
            {error}
          </div>
        )}

        {/* Title row with session count on the right */}
        <div
          className="mb-6 flex flex-wrap items-center justify-between gap-4"
          data-tour="captures-summary"
        >
          <p className="text-gray-300 text-sm">
            {pagination.total > 0
              ? `Total ${pagination.total} Capture Session${pagination.total === 1 ? "" : "s"} • newest first`
              : isLoading
                ? "Loading…"
                : "No capture sessions yet"}
          </p>
          {pagination.total > 0 && totalPages > 1 && (
            <PaginationControls
              page={page}
              totalPages={totalPages}
              canPrev={canPrev}
              canNext={canNext}
              isLoading={isLoading}
              onPageChange={handlePageChange}
              tourTarget="captures-pagination"
            />
          )}
        </div>

        {isLoading && sessions.length === 0 ? (
          <div className="text-center text-white py-24 text-lg">Loading…</div>
        ) : sessions.length === 0 && !error ? (
          <div className="text-center text-gray-300 py-24">
            <div className="text-5xl mb-4">📭</div>
            <p>No capture sessions are available for your organization yet.</p>
          </div>
        ) : (
          <div>
            {sessions.map((session, index) => (
              <SessionCard
                key={session.id}
                session={session}
                isFirst={index === 0}
                onView={(image) => {
                  analytics.capture("image_viewer_opened", { source: "captures" });
                  setViewerImage(image);
                }}
              />
            ))}
          </div>
        )}

        {pagination.total > 0 && totalPages > 1 && (
          <PaginationControls
            page={page}
            totalPages={totalPages}
            canPrev={canPrev}
            canNext={canNext}
            isLoading={isLoading}
            onPageChange={handlePageChange}
            className="mt-8"
          />
        )}
      </div>
      <ImageViewerModal image={viewerImage} onClose={() => setViewerImage(null)} />
    </div>
  );
};

export default CapturedImagesPage;
